import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import welcomeSplash from '@/assets/welcome-splash.webp';
import logoMark from '@/assets/logo-fennec-cart-mark.webp';
import { useSettings } from '@/contexts/SettingsContext';

const WelcomeSplash = () => {
  const [show, setShow] = useState(false);
  const { language } = useSettings();

  const splashSeenKey = 'splash_seen_date_v3';
  const copy = language === 'ar'
    ? {
        title: 'مرحباً بك',
        subtitle: 'في عالم التسوق والخدمات المميزة',
        skip: 'اضغط للمتابعة',
        imageAlt: 'صورة ترحيبية',
      }
    : language === 'fr'
      ? {
          title: 'Bienvenue',
          subtitle: 'Dans l’univers du shopping et des services premium',
          skip: 'Touchez pour continuer',
          imageAlt: 'Image de bienvenue',
        }
      : {
          title: 'Welcome',
          subtitle: 'To a world of shopping and premium services',
          skip: 'Tap to continue',
          imageAlt: 'Welcome image',
        };

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    const seenDate = localStorage.getItem(splashSeenKey);

    if (seenDate !== today) {
      setShow(true);
      localStorage.setItem(splashSeenKey, today);
    }
  }, []);

  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => setShow(false), 4500);
      return () => clearTimeout(timer);
    }
  }, [show]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden cursor-pointer"
          onClick={() => setShow(false)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Background image */}
          <motion.div
            className="absolute inset-0"
            initial={{ scale: 1.3 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.5, ease: 'easeOut' }}
          >
            <img
              src={welcomeSplash}
              alt={copy.imageAlt}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/40" />
          </motion.div>

          {/* Particle effects */}
          <div className="absolute inset-0 pointer-events-none">
            {Array.from({ length: 30 }).map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1.5 h-1.5 rounded-full"
                style={{
                  background: i % 3 === 0 ? '#fbbf24' : i % 3 === 1 ? '#f97316' : '#ffffff',
                  left: '50%',
                  top: '50%',
                  boxShadow: `0 0 6px ${i % 3 === 0 ? '#fbbf24' : '#f97316'}`,
                }}
                initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
                animate={{
                  x: (Math.random() - 0.5) * 500,
                  y: (Math.random() - 0.5) * 800,
                  opacity: [0, 1, 1, 0],
                  scale: [0, 1.5, 1, 0],
                }}
                transition={{
                  duration: 2 + Math.random() * 2,
                  delay: 0.3 + Math.random() * 0.8,
                  ease: 'easeOut',
                }}
              />
            ))}
          </div>

          {/* Content */}
          <div className="relative z-10 text-center px-6 max-w-md">
            {/* Logo burst */}
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.3 }}
              className="mb-6"
            >
              <motion.div
                className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-amber-400 to-orange-600 flex items-center justify-center shadow-[0_0_40px_rgba(249,115,22,0.6)] p-4"
                animate={{
                  boxShadow: [
                    '0 0 40px rgba(249,115,22,0.6)',
                    '0 0 80px rgba(249,115,22,0.8)',
                    '0 0 40px rgba(249,115,22,0.6)',
                  ],
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <img src={logoMark} alt="Logo" className="w-full h-full object-contain" />
              </motion.div>
            </motion.div>

            {/* Title */}
            <motion.h1
              className="text-3xl font-black text-white mb-3 leading-tight"
              style={{ textShadow: '0 0 30px rgba(249,115,22,0.8), 0 2px 10px rgba(0,0,0,0.5)' }}
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.8, ease: 'easeOut' }}
            >
              {copy.title}
            </motion.h1>

            <motion.p
              className="text-lg text-amber-200 font-bold mb-2"
              style={{ textShadow: '0 0 20px rgba(251,191,36,0.5)' }}
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.9, duration: 0.8 }}
            >
              {copy.subtitle}
            </motion.p>


            {/* Decorative line */}
            <motion.div
              className="h-0.5 bg-gradient-to-r from-transparent via-amber-400 to-transparent mx-auto mt-4"
              initial={{ width: 0 }}
              animate={{ width: '80%' }}
              transition={{ delay: 1.6, duration: 1 }}
            />

            {/* Business DZ branding */}
            <motion.p
              className="mt-4 text-sm text-white/70 font-medium"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2 }}
            >
              Marché DZ
            </motion.p>

            {/* Skip hint */}
            <motion.p
              className="mt-6 text-xs text-white/40"
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 1, 0.5, 1] }}
              transition={{ delay: 2.5, duration: 2 }}
            >
              {copy.skip}
            </motion.p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WelcomeSplash;
